import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import sourceService from "../services/source";
import { formatDate } from "../utils";
import { saveCredentials } from "../utils/credentialManager";

const initialState = {
  sources: [],
  currentSource: {},
  isLoading: false
};

export const getSources = createAsyncThunk(
  "source/get",
  async (sourceInfo) => {
    const res = await sourceService.getSources(sourceInfo);
    return res.data;
  }
)

export const getSource = createAsyncThunk(
  "source/get-source",
  async (sourceInfo) => {
    const res = await sourceService.getSource(sourceInfo);
    return res.data;
  }
)

export const createSource = createAsyncThunk(
  "source/create",
  async (sourceInfo) => {
    const res = await sourceService.createSource(sourceInfo);
    return res.data;
  }
);

export const updateSource = createAsyncThunk(
  "source/update",
  async ({ sourceId, sourceInfo }) => {
    const res = await sourceService.updateSource(sourceId, sourceInfo);
    return res.data;
  }
);

export const deleteSource = createAsyncThunk(
  "source/delete",
  async (id) => {
    const res = await sourceService.deleteSource(id);
    return res.data;
  }
);

export const checkSourceAuth = createAsyncThunk(
  "source/check-auth",
  async (sourceInfo) => {
    const res = await sourceService.checkSourceAuth(sourceInfo);
    return res.data;
  }
)

export const saveUserCredentials = createAsyncThunk(
  "source/save-user-credentials",
  async (credentialInfo) => {
    const res = await sourceService.saveUserCredentials(credentialInfo);
    return res.data;
  }
)

export const deactivateUserCredentials = createAsyncThunk(
  "source/deactivate-user-credentials",
  async (sourceId) => {
    const res = await sourceService.deactivateUserCredentials(sourceId);
    return res.data;
  }
)

export const getSourceWithCredentials = createAsyncThunk(
  "source/get-with-credentials",
  async (sourceName) => {
    const res = await sourceService.getSourceWithCredentials(sourceName);
    return res.data;
  }
)

export const testOracleEpmConnection = createAsyncThunk(
  "source/test-connection",
  async (connectionInfo) => {
    const res = await sourceService.testOracleEpmConnection(connectionInfo);
    return res.data;
  }
)

export const syncOracleEpmMetadata = createAsyncThunk(
  "source/sync-oracle-epm-metadata",
  async (syncInfo) => {
    const res = await sourceService.syncOracleEpmMetadata(syncInfo);
    return res.data;
  }
)

export const createOracleEpmConfig = createAsyncThunk(
  "source/create-oracle-epm-config",
  async (configInfo) => {
    const res = await sourceService.createOracleEpmConfig(configInfo);
    return res.data;
  }
)

export const getOracleEpmConfigBySource = createAsyncThunk(
  "source/get-oracle-epm-config-by-source",
  async (sourceId) => {
    const res = await sourceService.getOracleEpmConfigBySource(sourceId);
    return res.data;
  }
)

export const getCubesBySource = createAsyncThunk(
  "source/get-cubes-by-source",
  async ({ sourceId, page = 1, pageSize = 10 }) => {
    const res = await sourceService.getCubesBySource(sourceId, page, pageSize);
    return res.data;
  }
)

const sourceSlice = createSlice({
  name: 'source',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder.addCase(getSources.fulfilled, (state, action) => {
      state.sources = action.payload;
    })
    builder.addCase(getSource.fulfilled, (state, action) => {
      state.currentSource = action.payload;
    })
    builder.addCase(createSource.fulfilled, (state, action) => {
      const {id, name, type, created_at} = action.payload;
      const date = formatDate(created_at);
      state.sources.push({
        id, 
        name, 
        type,
        created_at: date
      })
      console.log(action.payload)
    })
    builder.addCase(updateSource.fulfilled, (state, action) => {
      const {id, name, type, created_at} = action.payload;
      const date = formatDate(created_at);
      const sourceIndex = state.sources.findIndex(source => source.id === id);
      if (sourceIndex !== -1) {
        state.sources[sourceIndex] = {
          id, 
          name, 
          type,
          created_at: date
        };
      }
      console.log('Source updated:', action.payload);
    })
    builder.addCase(deleteSource.fulfilled, (state, action) => {
      const {success, id} = action.payload;
      if(success) {
        const newSource = state.sources.filter(item => item.id!==id);
        state.sources = newSource;
      }
    })
    builder.addCase(checkSourceAuth.fulfilled, (state, action) => {
      const { name, username, password, is_connected } = action.payload;

      if(is_connected) {
        // Use utility function to save credentials with expiration
        saveCredentials(name, username, password);
        window.alert('Successfully saved.')
      }
      else
        window.alert('Credential is not valid')
    })
    builder.addCase(testOracleEpmConnection.fulfilled, (state, action) => {
      // Test connection result is handled in the component
      console.log('Oracle EPM test connection result:', action.payload);
    })
    builder.addCase(testOracleEpmConnection.rejected, (state, action) => {
      // Test connection error is handled in the component
      console.log('Oracle EPM test connection error:', action.error);
    })
    builder.addCase(syncOracleEpmMetadata.fulfilled, (state, action) => {
      // Sync metadata result is handled in the component
      console.log('Oracle EPM sync metadata result:', action.payload);
    })
    builder.addCase(syncOracleEpmMetadata.rejected, (state, action) => {
      // Sync metadata error is handled in the component
      console.log('Oracle EPM sync metadata error:', action.error);
    })
    builder.addCase(createOracleEpmConfig.fulfilled, (state, action) => {
      // Oracle EPM config creation result is handled in the component
      console.log('Oracle EPM config creation result:', action.payload);
    })
    builder.addCase(createOracleEpmConfig.rejected, (state, action) => {
      // Oracle EPM config creation error is handled in the component
      console.log('Oracle EPM config creation error:', action.error);
    })
    builder.addCase(getOracleEpmConfigBySource.fulfilled, (state, action) => {
      // Oracle EPM config fetch result is handled in the component
      console.log('Oracle EPM config fetch result:', action.payload);
    })
    builder.addCase(getOracleEpmConfigBySource.rejected, (state, action) => {
      // Oracle EPM config fetch error is handled in the component
      console.log('Oracle EPM config fetch error:', action.error);
    })
    builder.addCase(getCubesBySource.fulfilled, (state, action) => {
      // Cubes fetch result is handled in the component
      console.log('Cubes fetch result:', action.payload);
    })
    builder.addCase(getCubesBySource.rejected, (state, action) => {
      // Cubes fetch error is handled in the component
      console.log('Cubes fetch error:', action.error);
    })
  }
})

const { reducer } = sourceSlice;
export default reducer;